import { create } from "zustand";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

const POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || "ap-northeast-1_Ti6uAjngG";
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || "1mluir2p6j5pskl7ju0nplhuoq";

const userPool = new CognitoUserPool({
  UserPoolId: POOL_ID,
  ClientId: CLIENT_ID,
});

export interface AuthUser {
  email: string;
  sub: string;
  idToken: string;
}

const EMAIL_STORAGE_KEY = "glossika-email-signup";

interface AuthState {
  user: AuthUser | null;
  emailUser: string | null;
  loading: boolean;
  error: string | null;
  showModal: boolean;
  showEmailModal: boolean;

  checkSession: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ needsConfirmation: boolean }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  emailSignUp: (email: string) => Promise<"new" | "exists">;
  signOut: () => void;
  openModal: () => void;
  closeModal: () => void;
  openEmailModal: () => void;
  closeEmailModal: () => void;
  clearError: () => void;
}

function extractUser(session: CognitoUserSession): AuthUser {
  const payload = session.getIdToken().decodePayload();
  return {
    email: (payload["email"] as string) ?? "",
    sub: (payload["sub"] as string) ?? "",
    idToken: session.getIdToken().getJwtToken(),
  };
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  emailUser: null,
  loading: true,
  error: null,
  showModal: false,
  showEmailModal: false,

  checkSession: () => {
    const savedEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      set({ user: null, emailUser: savedEmail, loading: false });
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        set({ user: null, emailUser: savedEmail, loading: false });
        return;
      }
      set({ user: extractUser(session), emailUser: savedEmail, loading: false });
    });
  },

  signIn: (email, password) =>
    new Promise<void>((resolve, reject) => {
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
      const authDetails = new AuthenticationDetails({ Username: email, Password: password });

      set({ loading: true, error: null });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          set({ user: extractUser(session), loading: false, showModal: false, error: null });
          resolve();
        },
        onFailure: (err) => {
          set({ error: err.message ?? "Sign in failed", loading: false });
          reject(err);
        },
      });
    }),

  signUp: (email, password) =>
    new Promise((resolve, reject) => {
      set({ loading: true, error: null });

      const attrs = [new CognitoUserAttribute({ Name: "email", Value: email })];

      userPool.signUp(email, password, attrs, [], (err, result) => {
        if (err) {
          set({ error: err.message ?? "Sign up failed", loading: false });
          reject(err);
          return;
        }
        set({ loading: false });
        resolve({ needsConfirmation: !result?.userConfirmed });
      });
    }),

  confirmSignUp: (email, code) =>
    new Promise<void>((resolve, reject) => {
      set({ loading: true, error: null });
      const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });

      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) {
          set({ error: err.message ?? "Confirmation failed", loading: false });
          reject(err);
          return;
        }
        set({ loading: false });
        resolve();
      });
    }),

  emailSignUp: (email) =>
    new Promise((resolve) => {
      set({ loading: true, error: null });
      const attrs = [new CognitoUserAttribute({ Name: "email", Value: email })];
      userPool.signUp(email, "x", attrs, [], (err) => {
        if (err && err.name === "UsernameExistsException") {
          set({ loading: false });
          resolve("exists");
          return;
        }
        localStorage.setItem(EMAIL_STORAGE_KEY, email);
        set({ emailUser: email, loading: false, showEmailModal: false });
        resolve("new");
      });
    }),

  signOut: () => {
    const cognitoUser = userPool.getCurrentUser();
    cognitoUser?.signOut();
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    set({ user: null, emailUser: null });
  },

  openModal: () => set({ showModal: true, error: null }),
  closeModal: () => set({ showModal: false, error: null }),
  openEmailModal: () => set({ showEmailModal: true, error: null }),
  closeEmailModal: () => set({ showEmailModal: false, error: null }),
  clearError: () => set({ error: null }),
}));
